// src/components/ErrorBoundary.jsx
import React from "react";
import * as Sentry from "@sentry/react";
import { useRouteError, useNavigate, isRouteErrorResponse } from "react-router-dom";
import { tekst as jmTekst, vertaal as jmVertaal } from "../lib/woorden";
import { hoekMaat } from "../lib/hoek";

// ── Generic inline fallback (used inside pages / widget areas) ──────────────
export const ErrorFallback = ({ error, resetError }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      padding: "2rem",
      textAlign: "center",
      border: "1px solid #fee2e2",
      borderRadius: hoekMaat(8),
      backgroundColor: "#fff5f5",
      margin: "1rem",
    }}
  >
    <h2 style={{ color: "#dc2626", marginBottom: "0.5rem" }}>
      {jmVertaal("Er ging iets mis")}
    </h2>
    {/* Hier stond de rauwe javascript-foutmelding. Die zegt de gast niets en kan zomaar een adres
        of een stuk gegevens bevatten; hij gaat wel naar Sentry, want daar hoort hij. (WI6) */}
    <p style={{ color: "#6b7280", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
      {jmVertaal("Probeer het opnieuw. Blijft het misgaan, neem dan contact op met de winkel.")}
    </p>
    <button
      onClick={resetError}
      style={{
        padding: "0.5rem 1.5rem",
        backgroundColor: "#dc2626",
        color: "white",
        border: "none",
        borderRadius: hoekMaat(6),
        cursor: "pointer",
        fontSize: "0.9rem",
      }}
    >
      {jmVertaal("Opnieuw proberen")}
    </button>
  </div>
);

// ── Full-page fallback for React Router errorElement ────────────────────────
export const RouteErrorFallback = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const is404 =
    isRouteErrorResponse(error) && error.status === 404;

  // De rauwe foutmelding gaat naar Sentry, niet naar de gast. (WI6)
  const message = is404
    ? "Het adres dat je opende bestaat niet."
    : "Probeer het opnieuw. Blijft het misgaan, neem dan contact op met de winkel.";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "2rem",
        textAlign: "center",
        backgroundColor: "#fff5f5",
      }}
    >
      <h1 style={{ color: "#dc2626", fontSize: "2rem", marginBottom: "0.5rem" }}>
        {is404 ? "Deze pagina bestaat niet" : "Er ging iets mis"}
      </h1>
      <p style={{ color: "#6b7280", marginBottom: "2rem", maxWidth: "400px" }}>
        {message}
      </p>
      <div style={{ display: "flex", gap: "1rem" }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "0.5rem 1.5rem",
            backgroundColor: "#6b7280",
            color: "white",
            border: "none",
            borderRadius: hoekMaat(6),
            cursor: "pointer",
          }}
        >
          {jmVertaal("Go Back")}
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.5rem 1.5rem",
            backgroundColor: "#dc2626",
            color: "white",
            border: "none",
            borderRadius: hoekMaat(6),
            cursor: "pointer",
          }}
        >
          {jmVertaal("Pagina opnieuw laden")}
        </button>
      </div>
    </div>
  );
};

// ── Sentry-backed ErrorBoundary (wraps any subtree) ─────────────────────────
const ErrorBoundary = ({ children, fallback }) => (
  <Sentry.ErrorBoundary
    fallback={fallback || ErrorFallback}
    onError={(error, componentStack) => {
      Sentry.withScope((scope) => {
        scope.setTag("error.type", "react_boundary");
        scope.setExtra("componentStack", componentStack);
        Sentry.captureException(error);
      });
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;